import { useEffect, useState } from "react";

import { ArrowPathIcon } from "@heroicons/react/24/outline";

import ConfirmModal from "../shared/Confirm/ConfirmModal";

interface IRelistConfirmModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** How many listings the relist covers - drives the wording. */
  count: number;
  /** How many of them carry staged changes. */
  stagedCount?: number;
  onConfirm: (appendSku: boolean) => void;
  isLoading: boolean;
}

/**
 * The one confirm every relist on the optimisation page goes through, carrying the Append SKU
 * option. Staged changes are applied first, then each listing is withdrawn and published again.
 */
export default function RelistConfirmModal({
  open,
  setOpen,
  count,
  stagedCount,
  onConfirm,
  isLoading,
}: IRelistConfirmModalProps) {
  const [appendSku, setAppendSku] = useState(false);

  useEffect(() => {
    if (open) setAppendSku(false);
  }, [open]);

  const plural = count === 1 ? "listing" : "listings";
  const stagedLine =
    stagedCount == null
      ? ""
      : stagedCount === count
        ? `\nAll ${count} carry staged changes, which are applied first.`
        : stagedCount === 0
          ? "\nNone of them carry staged changes, so they are relisted as they are."
          : `\n${stagedCount} of them carry staged changes, which are applied first; the rest are relisted as they are.`;

  return (
    <ConfirmModal
      open={open}
      setOpen={setOpen}
      title={count === 1 ? "Relist this listing?" : `Relist ${count} ${plural}?`}
      message={`Each listing is withdrawn from eBay and published again as a new listing.${stagedLine}`}
      confirmButtonText={"Relist"}
      onConfirm={() => onConfirm(appendSku)}
      isLoading={isLoading}
      Icon={<ArrowPathIcon className="h-6 w-6 text-green-700" aria-hidden="true" />}
      iconBg="bg-green-100"
    >
      <label className="mt-3 flex items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={appendSku}
          onChange={(e) => setAppendSku(e.target.checked)}
        />
        <span>
          <span className="font-medium">Append SKU</span>
          <span className="block text-xs text-gray-500">
            Publish with a "-r1", "-r2", etc. suffix on the SKU to improve marketplace visibility. Tag numbers and scanning are unaffected.
          </span>
        </span>
      </label>
    </ConfirmModal>
  );
}
